import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, defaultApiParamStr, queryClient } from '@/_shared';

import type {
  ConferenceEventCreateSchema,
  ConferenceEventQueryAllSchema,
  ConferenceEventQuerySchema,
  ConferenceEventUpdateSchema,
} from './schemas';

export function getConferencesAndEventsQuery(apiParamStr = defaultApiParamStr) {
  return {
    queryKey: ['CONFERENCES_AND_EVENTS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<ConferenceEventQueryAllSchema>({
        endpoint: '/api/v1/conference-events',
        params: `${apiParamStr}${apiParamStr.indexOf('include=') < 0 ? '&include=conference_host' : ''}`,
      });
      return res;
    },
  };
}

export function useGetConferencesAndEvents(apiParamStr: string) {
  return useSuspenseQuery(getConferencesAndEventsQuery(apiParamStr));
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['CONFERENCES_AND_EVENTS'] });
};

export const useGetConferencesAndEventsMutation: UseControlledMutation<ConferenceEventQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['CONFERENCES_AND_EVENTS_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<ConferenceEventQueryAllSchema>({
            endpoint: '/api/v1/conference-events',
            params: { search, per_page },
          });
          return orderBy(res?.data ?? [], ['conference_name']) || [];
        },
      });
    },
  });

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, ConferenceEventCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/conference-events/',
        payload,
      }),
  });

export function getConferenceAndEventQuery(id: string) {
  return {
    queryKey: ['CONFERENCE_AND_EVENT', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: ConferenceEventQuerySchema }>({
        endpoint: `/api/v1/conference-events/${id}`,
        params: {
          include: `team_member_attending.profile_image,attachments,attendees.profile_images,brand,conference_host,physical_address.state,physical_address.country`,
        },
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export const invalidateConferenceEventQuery = (id: string) => {
  queryClient.invalidateQueries({ queryKey: ['CONFERENCE_AND_EVENT', id] });
};

export function useGetConferenceAndEvent(id: string) {
  return useSuspenseQuery(getConferenceAndEventQuery(id));
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: ConferenceEventQuerySchema }, ApiError, Partial<ConferenceEventUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/conference-events/${id}`,
        method: 'PUT',
        payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'conference-events', id] });
    },
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['CONFERENCE_AND_EVENT_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/conference-events/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
